import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { QUOTE_LIMIT_OPTIONS } from './usePullQuoteSelection';

import type { QuoteLimit } from './usePullQuoteSelection';

type Props = {
  quoteLimit: QuoteLimit;
  onLimitChange: (limit: QuoteLimit) => void;
};

export default function PullQuoteLimitDropdown({
  quoteLimit,
  onLimitChange,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedLabel =
    QUOTE_LIMIT_OPTIONS.find(opt => opt.value === quoteLimit)?.label || '';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (value: QuoteLimit) => {
    onLimitChange(value);
    setIsOpen(false);
  };

  return (
    <Wrapper ref={dropdownRef}>
      <TriggerButton onClick={() => setIsOpen(!isOpen)}>
        <TriggerLabel>{selectedLabel}</TriggerLabel>
        <Caret $isOpen={isOpen}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path
              d="M9 5.5L5 1.5L1 5.5"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        </Caret>
      </TriggerButton>
      {isOpen && (
        <Menu>
          {QUOTE_LIMIT_OPTIONS.map(option => (
            <MenuItem
              key={String(option.value)}
              $isSelected={option.value === quoteLimit}
              onClick={() => handleSelect(option.value)}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
`;

const TriggerButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 4px 8px;
  border-radius: var(--radius);
  cursor: pointer;
  transition: all 150ms ease-out;

  &:hover {
    background-color: var(--surface-elevated);
  }

  &:active {
    scale: 0.98;
  }
`;

const TriggerLabel = styled.span`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 100%;
`;

const Caret = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  color: var(--accent);
  width: 10px;
  height: 6px;
  transition: transform var(--transition-fast) ease;
  ${props =>
    !props.$isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: max-content;
  display: flex;
  flex-direction: column;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 4px;
  z-index: 100;
  box-shadow: var(--elevation-toast);
`;

const MenuItem = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  white-space: nowrap;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  color: ${props =>
    props.$isSelected ? 'var(--primary-hover)' : 'var(--text-secondary)'};

  &:hover {
    background-color: var(--border);
  }
`;
